'use client';

import { useSearchParams } from 'next/navigation';
import { useContext, useEffect, useState } from 'react';

import {
    IconName,
    Section,
    Spacer,
    SpacerSize,
    Title,
    TitleLevels,
    TitleMarkup,
    ToasterContext,
} from '@bytel/trilogy-react-ts';
import { ToasterPosition } from '@bytel/trilogy-react-ts/lib/components/toaster/ToasterProps';

import { ConfigType } from '@app-types/api';
import { FaiProduct } from '@app-types/fai';
import { JOURNEYS, OptionType, PlanType, RepeaterProduct } from '@app-types/product';
import { TagEventType } from '@app-types/tracking';

import CatalogOffersLink from '@components/home/catalog-offers-link';
import CustomContainer from '@components/layout/custom-container';
import { TriggerLogin } from '@components/trigger-login';

import { useScrollBelow } from '@hooks/use-below';
import useTracking from '@hooks/use-tracking';

import { tagHome, tagHomeFai } from '@constants/tags';

import { HomeTabs } from '@landing/components';
import { useHome } from '@landing/hooks';

import { MobileAndFaiFaqType } from '../forfaits-mobiles/sans-engagement/page';
import PageBottomContent from './home/bottom-content/page-bottom-content';
import HomePageConfigurators from './home/home-page-configurators';
import { ProductsCatalog } from './products-catalog';
import StickySummary from './sticky-footer/sticky-summary';

export function Configurator({
    products,
    faqs,
    faiProducts,
    features,
    blockedOption,
    config,
    repeaters,
}: Readonly<{
    products: { offers: PlanType[]; catalog: PlanType[] };
    faqs: MobileAndFaiFaqType;
    faiProducts: FaiProduct[];
    features: Record<string, boolean>;
    blockedOption?: OptionType;
    config?: ConfigType;
    repeaters?: RepeaterProduct[];
}>) {
    const [shouldShowCatalogOffers, setShouldShowCatalogOffers] = useState(false);
    const { otisConfigurator, setOtisConfigurator, setSelectedFaiProduct } = useHome();
    const { sendTag } = useTracking();
    const { isBelow, ref: sectionRef } = useScrollBelow();
    const { show } = useContext(ToasterContext);

    const searchParams = useSearchParams();

    const isMobilePlanFirst = !otisConfigurator || otisConfigurator.value === JOURNEYS.BYOU;
    const isFeatureFaiBlockActivated = features.homePageFaiBlock || false;

    useEffect(() => {
        if (config?.toaster_title) {
            show({
                title: config?.toaster_title,
                description: config.toaster_description || '',
                duration: config.toaster_duration_ms || 6000,
                id: 'bundle-toaster',
                position: ToasterPosition.TOP,
                iconName: (config.toaster_icon as IconName) || IconName.INFOS_CIRCLE,
            });
        }
        if (searchParams.get('pure_fibre') === 'true') {
            setOtisConfigurator(JOURNEYS.FAI);
            if (faiProducts?.[0]) {
                setSelectedFaiProduct(faiProducts[0]);
            }
        }
    }, []);

    useEffect(() => {
        sendTag(otisConfigurator.value === JOURNEYS.BYOU ? tagHome : tagHomeFai, TagEventType.DATA);
    }, [otisConfigurator]);

    return (
        <>
            <TriggerLogin />
            <section className='section has-background-' ref={sectionRef}>
                {isFeatureFaiBlockActivated ? <HomeTabs /> : null}
                <Spacer size={SpacerSize.LARGE} />

                <CustomContainer>
                    <Title level={TitleLevels.ONE} markup={TitleMarkup.H1}>
                        {isMobilePlanFirst ? 'Nos forfaits B&YOU sans engagement' : 'Nos offres fibre sans engagement'}
                    </Title>
                    <HomePageConfigurators
                        plans={products.offers}
                        repeaters={repeaters}
                        faiProducts={faiProducts}
                        featureFaiBlockActivated={isFeatureFaiBlockActivated}
                    />
                </CustomContainer>
            </section>
            <StickySummary
                mobileFirst={isMobilePlanFirst}
                featureFaiBlockActivated={isFeatureFaiBlockActivated}
                isStickyVisible={isBelow}
                repeaters={repeaters}
            />
            <Spacer size={SpacerSize.LARGE} />

            <CatalogOffersLink
                onClick={() => setShouldShowCatalogOffers((val) => !val)}
                showCatalogOffers={shouldShowCatalogOffers}
            />
            <Spacer size={SpacerSize.LARGE} />
            {shouldShowCatalogOffers && (
                <Section>
                    <ProductsCatalog catalogue={products.catalog} blockedOption={blockedOption} />
                    <Spacer size={SpacerSize.HUGE} />
                </Section>
            )}
            <Section>
                <PageBottomContent mobilePlanFirst={isMobilePlanFirst} faqs={faqs} />
            </Section>
        </>
    );
}
